import { ApplicationRef, Component, OnInit } from '@angular/core'
import { FormBuilder, FormControl, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { I18nService } from '@containers/i18n/i18n.service'
import {
  getDefaultTranslationsState,
  getTranslationsList,
} from '@containers/i18n/store/i18n.selector'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { downloadBlob } from '@shared/shared.utils'
import ListTrait from '@traits/list.trait'
import { Map } from 'immutable'
import { combineLatest, Observable, Subscription } from 'rxjs'
import { filter, map, finalize } from 'rxjs/operators'
import {
  FetchTranslationsListRequest,
  UpdateField,
  UpdateTranslationManualy,
} from '../../store/i18n.actions'
import { ShowSnackbar } from '@shared/shared.actions'
import { getLanguagesList } from '@containers/languages/store/languages.selector'
import { Language } from '@containers/i18n/store/i18n.interface'
import { FetchLanguagesListRequest } from '@containers/languages/store/languages.actions'

@Component({
  selector: 'app-translations-list',
  templateUrl: './translations-list.component.html',
  styleUrls: ['./translations-list.component.scss'],
})
export class TranslationsListComponent extends ListTrait implements OnInit {
  currentLanguage: string
  isLoading: boolean
  columns: object[] = [
    {
      prop: 'key',
      name: 'KEY',
      sortable: true,
    },
    {
      prop: 'value',
      name: 'VALUE',
      sortable: true,
    },
    {
      width: '162px',
      name: '',
      template: 'actionsColumn',
    },
  ]
  defaultTranslations

  importForm = new FormControl('', Validators.required)

  private _subs: Array<Subscription> = []
  languages$: Observable<Array<Language>>
  importModalVisible = false

  constructor(
    private store: Store<any>,
    private activatedRoute: ActivatedRoute,
    public router: Router,
    private translateService: TranslateService,
    private i18nService: I18nService,
  ) {
    super(router)
    this.getState()
    this.getDefaultTranslations()
  }

  ngOnInit() {
    this.getParams()

    this.languages$ = this.store.pipe(
      select(getLanguagesList),
      filter((state) => Boolean(state)),
      map((state) => state.toJS()),
      map((state) => state.data),
    )

    this.store.dispatch<any>(new FetchLanguagesListRequest({}))
  }

  ngOnDestroy(): void {
    this._subs.filter((sub) => Boolean(sub)).forEach((sub) => sub.unsubscribe())
  }

  getParams() {
    const queryParams$ = this.activatedRoute.queryParams
    const currentLanguage$ = this.activatedRoute.paramMap.pipe(
      filter((paraMap) => paraMap.has('iso2code')),
      map((paraMap) => paraMap.get('iso2code')),
    )

    const sub = combineLatest([queryParams$, currentLanguage$]).subscribe(
      this.runFilter.bind(this),
    )

    this._subs.push(sub)
  }

  runFilter([queryParams, currentLanguage]) {
    this.currentLanguage = currentLanguage
    const { page, page_size } = this.state.get('queryParams').toJS()
    const params: any = { page, page_size, ...queryParams }

    this.store.dispatch<any>(
      new FetchTranslationsListRequest(
        {
          filters: params.keyword
            ? {
                or: [
                  { field: 'key', value: params.keyword },
                  { field: 'value', value: params.keyword },
                ],
              }
            : {},
          pageSize: parseInt(params.page_size),
          page: parseInt(params.page) - 1,
          sort: {
            order: params.sort?.toUpperCase(),
            prop: params.order_by,
          },
        },
        currentLanguage,
      ),
    )

    this.store.dispatch(
      new UpdateField('queryParams', params, 'translationsList'),
    )
  }

  private getState() {
    const s = this.store
      .pipe(select(getTranslationsList))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })

    this._subs.push(s)
  }

  public deleteTranslation(key: string) {
    this.i18nService.deleteKey(key, this.currentLanguage).subscribe((res) => {
      this.runFilter([{}, this.currentLanguage])
      this.store.dispatch(new UpdateTranslationManualy(this.currentLanguage))
    })
  }

  getDefaultTranslations() {
    const sub = this.store
      .pipe(
        select(getDefaultTranslationsState),
        filter((state) => Boolean(state)),
        map((state) => state.toJS()),
      )
      .subscribe((translations) => {
        this.defaultTranslations = translations
      })

    this._subs.push(sub)
  }

  export() {
    this.isLoading = true
    this.i18nService
      .exportTranslations(this.currentLanguage, this.defaultTranslations)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((res) => {
        downloadBlob(res)
      })
  }

  import(event) {
    this.isLoading = true
    const formData = new FormData(event.target)
    this.i18nService
      .importTranslation(formData, this.currentLanguage)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (res) => {
          this.runFilter([{}, this.currentLanguage])
          this.store.dispatch(
            new ShowSnackbar({
              type: 'success',
              message: this.translateService.instant('success.document.upload'),
            }),
          )
          this.store.dispatch(
            new UpdateTranslationManualy(this.currentLanguage),
          )
        },
        (error) => {
          this.store.dispatch(
            new ShowSnackbar({
              type: 'error',
              message: this.translateService.instant('error.document.upload'),
            }),
          )
        },
      )
  }

  onTabChange(lang: string) {
    this.router.navigate(['admin', 'i18n', lang, 'list'])
  }
}
